"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/browser";

/**
 * Client-side Sentry init for the static-export site.
 *
 * We use @sentry/browser directly rather than @sentry/nextjs: this site is
 * `output: "export"` (no server runtime), and the Next.js SDK's build-time
 * machinery (server/edge instrumentation, source-map upload plugin) assumes
 * a server build and an auth token we don't ship. Plain browser SDK in a
 * client component covers everything a static site can produce — unhandled
 * JS errors, unhandled promise rejections, and explicit captureException
 * calls (e.g. the contact copy button).
 *
 * Two Sentry projects, one bundle: the corporate page (/) and the product
 * page (/sheriff-ai/) report to separate DSNs, chosen by the path the
 * visitor landed on. If only one DSN is configured, it is used everywhere.
 * Every event also carries a `site` tag computed at error time, because
 * Next's client-side navigation can cross between the two pages after init.
 * If neither DSN is set, this is a no-op — same pattern as the backend.
 */

const DSN_CORPORATE = process.env.NEXT_PUBLIC_SENTRY_DSN_CORPORATE;
const DSN_SHERIFF = process.env.NEXT_PUBLIC_SENTRY_DSN_SHERIFF;

function siteFromPath(pathname: string): "sheriff-ai" | "corporate" {
  return pathname.startsWith("/sheriff-ai") ? "sheriff-ai" : "corporate";
}

export function SentryInit() {
  useEffect(() => {
    if (Sentry.getClient()) return; // already initialised (client-side nav)
    const onSheriff = siteFromPath(window.location.pathname) === "sheriff-ai";
    const dsn = (onSheriff ? DSN_SHERIFF : DSN_CORPORATE) ?? DSN_CORPORATE ?? DSN_SHERIFF;
    if (!dsn) return;
    Sentry.init({
      dsn,
      sendDefaultPii: false,
      beforeSend(event) {
        event.tags = { ...event.tags, site: siteFromPath(window.location.pathname) };
        return event;
      },
    });
  }, []);
  return null;
}
